import { connect, ConnectedProps } from "react-redux";
import { withRouter } from "react-router";
import { Dispatch } from "@reduxjs/toolkit";
import {
  Prediction,
  PriceDropViewedProperties,
  TripCategory,
  ViewedFlightListProperties,
} from "redmond";
import { isCorpTenant } from "@capone/common";

import { DesktopPricePrediction } from "./component";
import {
  allTripSummariesSelector,
  getViewedFlightListProperties,
  isWatchingSelector,
  maxFlightPriceSelector,
  predictionLoadingSelector,
  predictionSelector,
} from "../../../../reducer/selectors";

import { IStoreState } from "../../../../../../reducers/types";
import {
  getActiveFiltersCount,
  getHasSetFareClassFilter,
  getHasSetStopsOption,
  getTripCategory,
  hasUnsupportedPredictionFilters,
  initialFilterOptions,
} from "../../../../../search/reducer";
import { actions } from "../../../../../search/actions";
import { actions as shopActions } from "../../../../actions";
import {
  IDeleteWatch,
  ISetOpenFlightShopCalendarDesktop,
} from "../../../../actions/actions";
import {
  ITripSummariesById,
  getPriceDropProperties,
} from "../../../../reducer";
import {
  getSelectedAccountReferenceIdIfRedemptionEnabled,
  getUserIsPrimaryCardHolder,
} from "../../../../../rewards/reducer";
import { PricePredictionGradientVariantType } from "../../../../../../context/experiments";
import { pricePredictionGradientVariantSelector } from "../../../../reducer/selectors/experiments";
import { config } from "../../../../../../api/config";

const mapStateToProps = (state: IStoreState): IStateProps => {
  return {
    prediction: predictionSelector(state),
    tripCategory: getTripCategory(state),
    hasUnsupportedPredictionFilters: hasUnsupportedPredictionFilters(state),
    hasPredictionFilters:
      getHasSetStopsOption(state) || getHasSetFareClassFilter(state),
    data: {
      maxFlightPrice: maxFlightPriceSelector(state),
    },
    isWatching: isWatchingSelector(state),
    tripSummaries: allTripSummariesSelector(state),
    selectedRewardsAccountId:
      getSelectedAccountReferenceIdIfRedemptionEnabled(state),
    predictionLoading: predictionLoadingSelector(state),
    viewedForecastProperties: getViewedFlightListProperties(state),
    priceDropViewedProperties: getPriceDropProperties(state),
    pricePredictionGradientVariant:
      pricePredictionGradientVariantSelector(state),
    hasActiveFilters: getActiveFiltersCount(state) > 0,
    canRedeemRewards:
      !isCorpTenant(config.TENANT) || getUserIsPrimaryCardHolder(state),
  };
};

interface IStateProps {
  data: { maxFlightPrice: number };
  prediction: Prediction | null;
  tripCategory: TripCategory;
  hasUnsupportedPredictionFilters: boolean;
  hasPredictionFilters: boolean;
  isWatching: boolean;
  predictionLoading: boolean | null;
  tripSummaries: ITripSummariesById;
  selectedRewardsAccountId: string | undefined;
  viewedForecastProperties: ViewedFlightListProperties | null;
  priceDropViewedProperties: Omit<PriceDropViewedProperties, "page">;
  pricePredictionGradientVariant:
    | PricePredictionGradientVariantType
    | undefined;
  hasActiveFilters: boolean;
  canRedeemRewards: boolean;
}

interface IDispatchProps {
  resetFilters: () => void;
  deleteWatch: () => IDeleteWatch;
  setOpenFlightShopCalendarDesktop: (
    openFlightShopCalendarDesktop: boolean
  ) => ISetOpenFlightShopCalendarDesktop;
}

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
  deleteWatch: () => dispatch(shopActions.deleteWatch()),
  resetFilters: () => {
    dispatch(actions.setStopsOption(initialFilterOptions.stopsOption));
    dispatch(actions.setAirlineFilter(initialFilterOptions.airlineFilter));
    dispatch(actions.setMaxPriceFilter(initialFilterOptions.maxPriceFilter));
    dispatch(actions.setAirportFilter(initialFilterOptions.airportFilter));
    dispatch(actions.setPolicyFilter(initialFilterOptions.policyFilter));
    dispatch(
      actions.setFlightNumberFilter(initialFilterOptions.flightNumberFilter)
    );
    dispatch(
      actions.setOutboundArrivalTimeRange(
        initialFilterOptions.outboundArrivalTimeRange
      )
    );
    dispatch(
      actions.setOutboundDepartureTimeRange(
        initialFilterOptions.outboundDepartureTimeRange
      )
    );
    dispatch(
      actions.setReturnDepartureTimeRange(
        initialFilterOptions.returnDepartureTimeRange
      )
    );
    dispatch(
      actions.setReturnArrivalTimeRange(
        initialFilterOptions.returnArrivalTimeRange
      )
    );
    dispatch(
      actions.setFareclassOptionFilter(
        initialFilterOptions.fareclassOptionFilter
      )
    );
    dispatch(actions.setApplyUserFlightPreferences(false));
    dispatch(actions.setUserPreferencesNotAvailable(false));
  },
  setOpenFlightShopCalendarDesktop: (openFlightShopCalendarDesktop: boolean) =>
    dispatch(
      shopActions.setOpenFlightShopCalendarDesktop(
        openFlightShopCalendarDesktop
      )
    ),
});

interface IOwnProps {
  hidden?: boolean;
  highlightedRewards?: boolean;
  useLockPriceLanguage?: boolean;
}

const mergeProps = (
  stateProps: IStateProps,
  dispatchProps: IDispatchProps,
  ownProps: IOwnProps
) => {
  return {
    ...stateProps,
    ...dispatchProps,
    resetFilters: () => dispatchProps.resetFilters(),
    ...ownProps,
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps, mergeProps);

export type DesktopPricePredictionConnectorProps = ConnectedProps<
  typeof connector
>;

export const ConnectedDesktopPricePrediction = connector(
  withRouter(DesktopPricePrediction)
);
